import React, { useCallback, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import {
  Backdrop,
  Button,
  CircularProgress,
  MenuItem,
  OutlinedInput,
  Select as SelectComponent,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsFormMode,
  selectedLmsItemId,
  setLmsFormMode,
} from '../../lmsSlice'
import {
  ParameterMappingsColumns as columns,
  lmsFormCreateMode,
  lmsFormEditMode,
  lmsFormNoneMode,
  ID,
  SUBMIT,
  loadingMessage,
  thopParameterNameField,
  thopParameterCodeField,
  resultTypeField,
} from '../../constants'
import { toast } from 'react-toastify'
import {
  addParameterMapping,
  getAllParameters,
  getTestCodesById,
  updateTestCode,
} from '../../../../services/LmsService'
import '../../lms.scss'
import * as Yup from 'yup'
import { ColumnType } from './CreateUpdateLabDevice'

type thopParameterDropDown = {
  id: string
  name: string
  short_form: string
  result_type: string
  test: string
}

const CreateUpdateParameterMapping = ({ model }: any) => {
  const mode = useAppSelector(selectedLmsFormMode)
  const selectedLMSItemId = useAppSelector(selectedLmsItemId)
  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState(false)

  const setDropDown = useCallback(async () => {
    columns?.forEach(async (column: ColumnType) => {
      switch (column?.name) {
        case thopParameterNameField: {
          setLoading(true)
          const response = await getAllParameters()
          if (response?.success) {
            let parametersData = response?.data?.lab_test?.map((data: any) => {
              return {
                id: data?.id,
                name: data?.name,
                short_form: data?.parameter_short_form,
                result_type: data?.test_result_type?.result_type,
                test: data?.panel?.name,
              }
            })
            parametersData = parametersData
              ?.filter((item: any) => {
                return (
                  item?.short_form &&
                  item?.result_type &&
                  item?.test &&
                  item?.name
                )
              })
              ?.sort((a: any, b: any) => a?.name?.localeCompare(b?.name))
            const uniqueNames = new Set()
            parametersData = parametersData?.filter((item: any) => {
              if (uniqueNames.has(item?.name)) {
                return false
              }
              uniqueNames.add(item?.name)
              return true
            })
            column.dropDown = Array?.isArray(parametersData)
              ? parametersData
              : []
            if (parametersData?.length > 0) setLoading(false)
          } else {
            toast?.error(response?.message)
          }
          break
        }
      }
    })
  }, [])

  async function validateParameterValues(values: any) {
    values.model_id = model?.id
    values.scaling_factor = parseFloat(values?.scaling_factor)
    if (mode === lmsFormCreateMode) {
      delete values[ID]
      addParameter(values)
    } else {
      updateParameter(values)
    }
  }

  async function addParameter(values: any) {
    const response = await addParameterMapping(values)
    if (response?.success) {
      toast?.success(response?.message)
      dispatch(setLmsFormMode(lmsFormNoneMode))
    } else {
      toast?.error(response?.message)
    }
  }

  async function updateParameter(values: any) {
    const response = await updateTestCode(values)
    if (response?.success) {
      toast?.success(response?.message)
      dispatch(setLmsFormMode(lmsFormNoneMode))
    } else {
      toast?.error(response?.message)
    }
  }

  const handleThopParameterSelect = (value: string, column: any) => {
    const selectedItem = value
    const selectedOption: thopParameterDropDown = column?.dropDown?.find(
      (opt: any) => opt?.name === selectedItem
    ) || {
      name: '',
      short_form: '',
      result_type: '',
      id: '',
      test: '',
    }
    setFieldValue(column?.name, selectedItem || '')
    setFieldValue(thopParameterCodeField, selectedOption?.short_form || '')
    setFieldValue(resultTypeField, selectedOption?.result_type || '')
  }

  const {
    values,
    setFieldValue,
    setValues,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues: {
      id: '',
      model_id: model?.id,
      channel_id: '',
      test_code: '',
      test_type: '',
      test_name: '',
      thop_parameter_code: '',
      thop_name: '',
      result_type: '',
      decimal_precision: '',
      scaling_factor: '',
    },
    validationSchema: Yup?.object({
      test_code: Yup?.string()?.required(),
      channel_id: Yup?.string()?.required(),
      test_type: Yup?.string()?.required(),
      test_name: Yup?.string()?.required(),
      thop_parameter_code: Yup?.string()?.required(),
      thop_name: Yup?.string()?.required(),
      decimal_precision: Yup?.string()?.required(),
      scaling_factor: Yup?.string()?.required(),
      result_type: Yup?.string()?.required(),
    }),
    onSubmit: (values) => {
      validateParameterValues(values)
    },
  })

  const getParameterMapping = useCallback(async () => {
    const response = await getTestCodesById(selectedLMSItemId)
    if (response?.success) {
      setValues(response?.data)
    } else {
      toast?.error(response?.message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLMSItemId, mode])

  useEffect(() => {
    if (mode === lmsFormEditMode) {
      getParameterMapping()
    }
  }, [mode, getParameterMapping])

  useEffect(() => {
    setDropDown()
  }, [mode, setDropDown])

  return (
    <>
      {loading && (
        <Backdrop className='backdrop' open={loading}>
          <Typography variant='h6' color='whitesmoke'>
            {loadingMessage}
          </Typography>
          <CircularProgress color='inherit' />
        </Backdrop>
      )}
      <form onSubmit={handleSubmit} className='labDevicesFormStyle'>
        <div className='labDeviceFormFirstDivStyle'>
          {columns?.map((column, index) => (
            <div className='labDeviceFormSecondDivStyle' key={index}>
              <div className='labDeviceFormThirdDivStyle'>
                {mode !== lmsFormNoneMode && (
                  <label
                    id={column?.name}
                    htmlFor={column?.name}
                    className='labDeviceFormLabelStyle fieldSet'
                  >
                    {column?.label}&nbsp;
                    {column?.mandatory && (
                      <span className='required_red'>*</span>
                    )}
                    &nbsp; :
                  </label>
                )}
              </div>
              <div className='labDeviceFormTextFieldDiv'>
                {!column?.dropDown ? (
                  <div className='formValidationErrorStyle'>
                    <TextField
                      className='labDeviceFormTextFieldStyle'
                      id={column?.name}
                      name={column?.name}
                      aria-labelledby={column?.name}
                      value={values?.[column?.name as keyof typeof values]}
                      onChange={handleChange}
                      disabled={
                        column?.name === thopParameterCodeField ||
                        column?.name === resultTypeField
                      }
                      error={
                        !!touched?.[column?.name as keyof typeof touched] &&
                        !!errors?.[column?.name as keyof typeof errors]
                      }
                      size='small'
                      onBlur={handleBlur}
                    />
                    {touched?.[column?.name as keyof typeof touched] &&
                      errors?.[column?.name as keyof typeof touched] && (
                        <Typography variant='caption' color='error'>
                          {String(
                            errors?.[column?.name as keyof typeof touched]
                          )}
                        </Typography>
                      )}
                  </div>
                ) : (
                  <div className='formValidationErrorStyle'>
                    <SelectComponent
                      id={column?.name}
                      className='labDeviceFormTextFieldStyle'
                      value={values?.[column?.name as keyof typeof values]}
                      name={column?.name}
                      aria-labelledby={column?.name}
                      onChange={(event) =>
                        handleThopParameterSelect(event?.target?.value, column)
                      }
                      input={<OutlinedInput className='selectFieldBorder' />}
                      onBlur={handleBlur}
                      error={
                        !!touched?.[column?.name as keyof typeof touched] &&
                        !!errors?.[column?.name as keyof typeof errors]
                      }
                    >
                      {Array?.isArray(column?.dropDown) &&
                        column?.dropDown?.map((option: any) => (
                          <MenuItem
                            key={crypto?.randomUUID()}
                            value={option?.name}
                          >
                            {option?.name}
                          </MenuItem>
                        ))}
                    </SelectComponent>
                    {touched?.[column?.name as keyof typeof touched] &&
                      errors?.[column?.name as keyof typeof touched] && (
                        <Typography variant='caption' color='error'>
                          {String(
                            errors?.[column?.name as keyof typeof touched]
                          )}
                        </Typography>
                      )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className='labDeviceFormSubmitDivButtonStyle'>
          <Button
            variant='contained'
            type='submit'
            size='small'
            className='labDeviceFormSubmitButtonStyle'
          >
            {SUBMIT}
          </Button>
        </div>
      </form>
    </>
  )
}

export default CreateUpdateParameterMapping
